export const OrderDetailsModal = ({ order, onClose, onStatusUpdate }) => {
  const statusSteps = [
    { value: 'processing', label: 'Processing', icon: '🎨', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'printed', label: 'Printed', icon: '🖨️', color: 'bg-orange-100 text-orange-800' },
    { value: 'quality_check', label: 'Quality Check', icon: '🔍', color: 'bg-amber-100 text-amber-800' },
    { value: 'packed', label: 'Packed', icon: '📦', color: 'bg-teal-100 text-teal-800' },
    { value: 'shipped', label: 'Shipped', icon: '🚚', color: 'bg-green-100 text-green-800' },
    { value: 'completed', label: 'Completed', icon: '🎉', color: 'bg-green-100 text-green-800' }
  ];

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getProductImage = (product) => {
    if (product?.images?.length > 0) {
      return product.images[0];
    }
    if (product?.design?.image) {
      return product.design.image;
    }
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Order Details</h2>
              <p className="text-gray-500">#{order.orderNumber}</p>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Order Summary */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-lg mb-3">Order Summary</h3>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Order Status</p>
                  <p className="font-medium capitalize">{order.status.replace('_', ' ')}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Order Date</p>
                  <p className="font-medium">{formatDate(order.createdAt)}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Store</p>
                  <p className="font-medium">{order.store?.name || 'N/A'}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Buyer</p>
                  <p className="font-medium">
                    {order.buyer?.firstName} {order.buyer?.lastName}
                  </p>
                </div>
              </div>
            </div>

            {/* Shipping Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-lg mb-3">Shipping Information</h3>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Recipient</p>
                  <p className="font-medium">
                    {order.shippingAddress.firstName} {order.shippingAddress.lastName}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="font-medium">
                    {order.shippingAddress.street}, {order.shippingAddress.city},<br />
                    {order.shippingAddress.state}, {order.shippingAddress.pincode},<br />
                    {order.shippingAddress.country}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Products */}
          <div className="mt-6">
            <h3 className="font-medium text-lg mb-3">Products</h3>
            
            <div className="space-y-4">
              {order.items.map((item, index) => {
                const image = getProductImage(item.product);
                return (
                  <div key={index} className="flex border-b pb-4 last:border-b-0">
                    <div className="flex-shrink-0 w-20 h-20 bg-gray-100 rounded-lg overflow-hidden">
                      {image ? (
                        <img 
                          src={image} 
                          alt={item.product?.name || 'Product'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          🛍️
                        </div>
                      )}
                    </div>
                    
                    <div className="ml-4 flex-1">
                      <h4 className="font-medium">{item.product?.name || 'Product'}</h4>
                      <div className="text-sm text-gray-500 mt-1">
                        <p>Material: {item.variant?.material || 'N/A'}</p>
                        <p>Color: {item.variant?.color?.name || 'N/A'}</p>
                        <p>Size: {item.variant?.size || 'N/A'}</p>
                        <p>Print Method: {item.printMethod || 'N/A'}</p>
                        <p>Quantity: {item.quantity || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Status History */}
          <div className="mt-6">
            <h3 className="font-medium text-lg mb-3">Status History</h3>
            
            <div className="space-y-4">
              {order.statusHistory.map((history, index) => (
                <div key={index} className="flex">
                  <div className="flex-shrink-0 mr-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100">
                      <span className="text-gray-500">{index + 1}</span>
                    </div>
                  </div>
                  <div className="flex-1 pb-4 border-b last:border-b-0">
                    <div className="flex justify-between">
                      <p className="font-medium capitalize">{history.status.replace('_', ' ')}</p>
                      <p className="text-sm text-gray-500">{formatDate(history.timestamp)}</p>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Updated by: {history.updatedBy === 'system' ? 'System' : 'Manufacturer'}
                    </p>
                    {history.notes && (
                      <p className="text-sm text-gray-500 mt-1">Notes: {history.notes}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Update Status (only show if order is not completed/cancelled) */}
          {!['completed', 'cancelled'].includes(order.status) && (
            <div className="mt-6">
              <h3 className="font-medium text-lg mb-3">Update Order Status</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {statusSteps.map((step) => {
                  if (step.value === order.status) return null;
                  return (
                    <button
                      key={step.value}
                      onClick={() => onStatusUpdate(order._id, step.value)}
                      className={`p-3 rounded-lg border ${step.color} hover:opacity-90 transition-opacity`}
                    >
                      <div className="flex items-center">
                        <span className="text-xl mr-2">{step.icon}</span>
                        <span>{step.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};